import { execFile } from 'node:child_process';
import type { AgentTool } from './tools';
import { forComponent } from './logging';

const log = forComponent('notify');

/**
 * PushNotification — node-only, OPT-IN OS desktop notification (CC parity: alert the user
 * out-of-band when a long task finishes or needs attention). Kept out of `defaultTools()` and
 * the edge-safe index (same policy as tools.shell.ts); the CLI mounts it explicitly.
 *
 * macOS: osascript `display notification` (no extra deps; terminal-notifier not required).
 * Linux: notify-send (libnotify). Other platforms / missing tools → tool reports unavailability
 * to the model instead of throwing (the task itself should not fail because a toast couldn't show).
 */
export function makeNotifyTool(opts: { platform?: NodeJS.Platform; exec?: typeof execFile } = {}): AgentTool {
  const platform = opts.platform ?? process.platform;
  const run = opts.exec ?? execFile;
  return {
    name: 'PushNotification',
    description:
      'Show an OS desktop notification to the user (out-of-band alert — e.g. a long task finished, input is needed). ' +
      'Use sparingly: only for events the user would want to be interrupted for.',
    parameters: {
      type: 'object',
      required: ['message'],
      properties: {
        message: { type: 'string', description: 'notification body' },
        title: { type: 'string', description: 'notification title (default "agentx")' },
      },
    },
    async run({ message, title }) {
      const msg = String(message ?? '').slice(0, 256);
      const head = String(title ?? 'agentx').slice(0, 64);
      if (!msg) return 'Error: empty message';
      const argv: [string, string[]] | null =
        platform === 'darwin'
          ? ['osascript', ['-e', `display notification ${JSON.stringify(msg)} with title ${JSON.stringify(head)}`]]
          : platform === 'linux'
            ? ['notify-send', [head, msg]]
            : null;
      if (!argv) return `Notifications unavailable on ${platform}.`;
      return new Promise<string>((resolve) => {
        run(argv[0], argv[1], { timeout: 5000 }, (e) => {
          if (e) { log.debug('notification failed', e); resolve(`Notification failed: ${e.message}`); }
          else resolve('Notification shown.');
        });
      });
    },
  };
}
